import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import { Typography, Flex, Button, Row, Col, notification, Modal } from "antd";
import Layout from "../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import { FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useRef } from "react";
import ProductionCheckInView from "./ProductionCheckInView";
const { Text } = Typography;

const ProductionCheckInReport = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [inspectionList, setInspectionList] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [plantOptions, setPlantOptions] = useState([]);
	const [plant, setPlant] = useState("All");
	const refDownload = useRef();
	const [apiNotification, contextHolder] = notification.useNotification();
	const currentProductionId = useRef(null);
	const [modalViewOpen, setModalViewOpen] = useState(false);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "",
			dataIndex: "edit",
			width: 50,
			render: (_, record) => {
				return (
					<Flex>
						<Button
							type="primary"
							danger
							icon={<FilePdfOutlined twoToneColor={"white"} />}
							onClick={(e) => history.push(`/production/pdf/${record.id}`)}
						/>
						&nbsp;
						<Button
							type="primary"
							icon={<EyeOutlined twoToneColor={"white"} />}
							onClick={() => {
								currentProductionId.current = record.id;
								setModalViewOpen(true);
							}}
						/>
					</Flex>
				);
			},
		},
		{
			title: "PRE Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "POST Date",
			dataIndex: "postDate",
			key: "postDate",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Responsible",
			dataIndex: "responsible",
			key: "responsible",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Closed By",
			dataIndex: "closedBy",
			key: "closedBy",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Subplant",
			dataIndex: "subplant",
			key: "subplant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
	];

	useEffect(() => {
		getInspectionList("");
		getPlants();
	}, []);

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let plantList = [{ value: "All", label: "All" }];
			data.payload.map((item) => {
				plantList.push({
					label: item.name,
					value: item._id,
				});
			});
			setPlantOptions(plantList);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const getInspectionList = async (term) => {
		setLoading(true);

		const start = `${startDate.year()}-${
			startDate.month() + 1
		}-${startDate.date()}`;
		const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

		const url =
			api +
			`/production/search?startDate=${start}&endDate=${end}&term=${term}&plant=${plant}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			if (data.success) {
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						responsible: item.responsible ? item.responsible.name : "-",
						closedBy: item.closedBy ? item.closedBy.name : "-",
						plant: item.plant ? item.plant.name : "-",
						subplant: item.subplant,
						type: item.type,
						date: dayjs(item.createdAt).format("MM/DD/YYYY HH:mm A"),
						postDate: item.postDate
							? dayjs(item.postDate).format("MM/DD/YYYY HH:mm A")
							: "N/A",
					});
				});
				setInspectionList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const onSearch = (value, _e, info) => {
		if (info?.source === "input") {
			getInspectionList(value);
		}
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const onChangePicker = (value) => {
		setPlant(value);
	};

	const headers = () => {
		return columns.slice(1).map((item) => {
			return {
				key: item.key,
				label: item.title,
			};
		});
	};

	const RenderSearch = () => {
		return (
			<CustomSearch
				startDate={startDate}
				endDate={endDate}
				dateFormat={"MM/DD/YYYY"}
				loading={loading}
				onRangeChange={onRangeChange}
				onSearch={onSearch}
				labelPicker={"status"}
				optionsPicker={plantOptions}
				onChangePicker={onChangePicker}
				defaultValuePicker={plant}
				pickerWidth={"200px"}
			/>
		);
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"}>
				<CustomTable columns={columns} data={inspectionList} isScroll={false} />
			</Flex>
		);
	};

	const RenderCSV = () => {
		return (
			<div>
				<CSVLink
					data={inspectionList}
					headers={headers()}
					filename={"57concrete-production-checkin.csv"}
					hidden
					ref={refDownload}
				>
					Download report
				</CSVLink>
				<Row>
					<Col span={24}>
						<Button
							type="primary"
							style={{ width: "100%" }}
							onClick={(e) => refDownload.current.link.click()}
							loading={loading}
							size="large"
						>
							Download Report
						</Button>
					</Col>
				</Row>
			</div>
		);
	};

	const ModalView = () => {
		return (
			<Modal
				title="Production Check In Data"
				open={modalViewOpen}
				onCancel={() => setModalViewOpen(false)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<ProductionCheckInView productionId={currentProductionId.current} />
			</Modal>
		);
	};

	return (
		<Layout title="Production CheckIn Report" description="Report">
			<Flex vertical gap={"large"}>
				<RenderSearch />
				{loading ? (
					<CustomSpinner />
				) : (
					<Row gutter={[16, 16]}>
						<Col span={24}>
							<RenderCSV />
						</Col>
						<Col span={24}>
							<RenderTable />
						</Col>
					</Row>
				)}
				{contextHolder}
				<ModalView />
			</Flex>
		</Layout>
	);
};

export default ProductionCheckInReport;
